<script lang="ts">
  import { Bodega, FarmaciaClinica, Farmacia, Bitacora, type Articulo, type Proveedor } from '../lib/api';
  import { parseSpreadsheet, csvCell } from '../lib/csv-import';
  import SearchSelect from './SearchSelect.svelte';
  import HelpHint from './HelpHint.svelte';

  let articulos = $state<Articulo[]>([]);
  let proveedores = $state<Proveedor[]>([]);
  let bajoMinimo = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let loading = $state(false);

  let nuevoArt = $state<Articulo>({ codigo: '', nombre: '', unidad: 'UN', stockMinimo: 0, activo: true });
  let nuevoProv = $state<Proveedor>({ nombre: '', nit: '', telefono: '', email: '', activo: true });
  let editandoArtId = $state<string | null>(null);
  let editandoProvId = $state<string | null>(null);
  let mov = $state({ articuloId: '', tipo: 'ENTRADA' as const, cantidad: 1, costoUnitario: 0, referencia: '', motivo: '' });
  let artQ = $state('');
  const articulosVisibles = $derived.by(() => {
    const q = artQ.trim().toLowerCase();
    const list = q ? articulos.filter(a => `${a.codigo} ${a.nombre}`.toLowerCase().includes(q)) : articulos;
    return list.slice(0, 10);
  });
  const artItems = $derived(articulos.map(a => ({ id: a.id!, label: `${a.codigo} ${a.nombre}` })));

  async function cargar() {
    loading = true; error = null;
    try {
      articulos = (await Bodega.articulos(bajoMinimo)).filter(a => a.activo);
      proveedores = (await Bodega.proveedores()).filter(p => p.activo);
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  function editarArticulo(a: Articulo) {
    editandoArtId = a.id ?? null;
    nuevoArt = { codigo: a.codigo, nombre: a.nombre, unidad: a.unidad ?? 'UN', stockMinimo: Number(a.stockMinimo ?? 0), activo: a.activo ?? true };
  }
  function editarProveedor(p: Proveedor) {
    editandoProvId = p.id ?? null;
    nuevoProv = { nombre: p.nombre, nit: p.nit ?? '', telefono: p.telefono ?? '', email: p.email ?? '', activo: p.activo ?? true };
  }
  function cancelarArticulo() { editandoArtId = null; nuevoArt = { codigo: '', nombre: '', unidad: 'UN', stockMinimo: 0, activo: true }; }
  function cancelarProveedor() { editandoProvId = null; nuevoProv = { nombre: '', nit: '', telefono: '', email: '', activo: true }; }

  async function guardarArticulo(e: SubmitEvent) {
    e.preventDefault();
    try {
      if (editandoArtId) { await Bodega.actualizarArt(editandoArtId, { ...nuevoArt }); }
      else { await Bodega.crearArt({ ...nuevoArt }); }
      nuevoArt = { codigo: '', nombre: '', unidad: 'UN', stockMinimo: 0, activo: true }; editandoArtId = null;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function guardarProveedor(e: SubmitEvent) {
    e.preventDefault();
    try {
      if (editandoProvId) { await Bodega.actualizarProv(editandoProvId, { ...nuevoProv }); }
      else { await Bodega.crearProv({ ...nuevoProv }); }
      nuevoProv = { nombre: '', nit: '', telefono: '', email: '', activo: true }; editandoProvId = null;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function registrarMov(e: SubmitEvent) {
    e.preventDefault();
    try { await Bodega.mover({ ...mov }); mov = { articuloId: '', tipo: 'ENTRADA', cantidad: 1, costoUnitario: 0, referencia: '', motivo: '' }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function eliminarArticulo(a: Articulo) {
    if (!confirm(`¿Eliminar el artículo ${a.nombre}?`)) return;
    try { await Bodega.eliminarArt(a.id!); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function eliminarProveedor(p: Proveedor) {
    if (!confirm(`¿Eliminar al proveedor ${p.nombre}?`)) return;
    try { await Bodega.eliminarProv(p.id!); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function trasladarAFarmacia(a: Articulo) {
    if (!a.id) return;
    const raw = window.prompt('Cantidad a trasladar a Farmacia POS:', '1');
    if (raw == null) return;
    const cantidad = Number(raw);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      error = 'Cantidad inválida';
      return;
    }
    error = null;
    info = null;
    try {
      await Bodega.mover({
        articuloId: a.id,
        tipo: 'SALIDA',
        cantidad,
        motivo: 'Traslado a farmacia POS',
      });
      const productos = await FarmaciaClinica.productos();
      const existente = productos.find(p => p.codigo.toLowerCase() === a.codigo.toLowerCase());
      if (existente?.id) {
        await FarmaciaClinica.actualizarProducto(existente.id, {
          ...existente,
          stock: Number(existente.stock ?? 0) + cantidad,
        });
      } else {
        await FarmaciaClinica.crearProducto({
          codigo: a.codigo,
          nombre: a.nombre,
          precio: Number(a.costoPromedio ?? 0),
          stock: cantidad,
          categoria: 'MEDICAMENTO',
          afectaInventario: true,
          activo: true,
        });
      }
      Bitacora.registrar({
        action: 'traslado.bodega_farmacia',
        resourceType: 'articulo',
        resourceId: a.id,
        details: { codigo: a.codigo, nombre: a.nombre, cantidad },
      }).catch(() => {});
      info = `Trasladado ${cantidad} de «${a.codigo}» a Farmacia POS.`;
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  async function trasladarAFarmaciaHosp(a: Articulo) {
    if (!a.id) return;
    const raw = window.prompt('Cantidad a trasladar a Farmacia hospitalaria (FAR-HOSP):', '1');
    if (raw == null) return;
    const cantidad = Number(raw);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      error = 'Cantidad inválida';
      return;
    }
    error = null;
    info = null;
    try {
      await Bodega.mover({
        articuloId: a.id,
        tipo: 'SALIDA',
        cantidad,
        motivo: 'Traslado a farmacia hospitalaria FAR-HOSP',
      });
      const meds = await Farmacia.meds();
      let med = meds.find(m => (m.codigo ?? '').toLowerCase() === a.codigo.toLowerCase());
      if (!med?.id) {
        med = await Farmacia.crearMed({
          codigo: a.codigo,
          nombre: a.nombre,
          presentacion: a.unidad ?? 'UN',
        });
      }
      const vencimiento = new Date();
      vencimiento.setFullYear(vencimiento.getFullYear() + 1);
      await Farmacia.crearLote({
        medicamentoId: med.id!,
        bodega: 'FAR-HOSP',
        numeroLote: `TR-${a.codigo}-${Date.now().toString().slice(-6)}`,
        vencimiento: vencimiento.toISOString().slice(0, 10),
        cantidad,
      });
      Bitacora.registrar({
        action: 'traslado.bodega_farmacia_hosp',
        resourceType: 'articulo',
        resourceId: a.id,
        details: { codigo: a.codigo, nombre: a.nombre, cantidad, bodega: 'FAR-HOSP' },
      }).catch(() => {});
      info = `Trasladado ${cantidad} de «${a.codigo}» a Farmacia hospitalaria (FAR-HOSP). El kárdex descuenta ese lote (FEFO), no la bodega otra vez.`;
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  async function importarCsvArticulos(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    error = null;
    try {
      const { rows } = await parseSpreadsheet(file);
      const existentes = await Bodega.articulos(false);
      let creados = 0;
      let actualizados = 0;
      for (const row of rows) {
        const codigo = csvCell(row, 'codigo', 'code');
        const nombre = csvCell(row, 'nombre', 'name');
        if (!codigo || !nombre) continue;
        const unidad = csvCell(row, 'unidad', 'unit') || 'UN';
        const stockMinimo = Number(csvCell(row, 'stockminimo', 'stock_minimo', 'minimo') || 0);
        const existente = existentes.find(a => a.codigo.toLowerCase() === codigo.toLowerCase());
        if (existente?.id) {
          await Bodega.actualizarArt(existente.id, { ...existente, nombre, unidad, stockMinimo, activo: true });
          actualizados++;
        } else {
          await Bodega.crearArt({ codigo, nombre, unidad, stockMinimo, activo: true });
          creados++;
        }
      }
      await cargar();
      info = `Importación artículos: ${creados} creados, ${actualizados} actualizados.`;
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      input.value = '';
    }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Bodega · Compras · Inventario</h2>
      <p class="muted">Artículos, movimientos y proveedores</p>
      <HelpHint title="Guía rápida · Bodega">
        <p>Busque por nombre (máx. 10). Traslade a Farmacia POS o a Farmacia hospitalaria (FAR-HOSP). Administrar en kárdex descuenta el lote FEFO, no vuelve a salir de bodega.</p>
      </HelpHint>
    </div>
    <div class="actions">
      <label class="chk"><input type="checkbox" bind:checked={bajoMinimo} onchange={cargar}/> Sólo bajo mínimos</label>
      <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
    </div>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <h3>Artículos</h3>
  <form onsubmit={guardarArticulo} class="row">
    <label class="field"><span>Código</span><input placeholder="Código" bind:value={nuevoArt.codigo} required disabled={editandoArtId !== null} /></label>
    <label class="field grow"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevoArt.nombre} required /></label>
    <label class="field"><span>Unidad</span><input placeholder="Unidad" bind:value={nuevoArt.unidad} style="width:80px"/></label>
    <label class="field"><span>Stock mín.</span><input type="number" placeholder="Stock mín." bind:value={nuevoArt.stockMinimo} style="width:110px"/></label>
    <button type="submit">{editandoArtId ? 'Guardar' : 'Agregar'}</button>
    {#if editandoArtId}<button type="button" class="ghost" onclick={cancelarArticulo}>Cancelar</button>{/if}
  </form>
  <div class="csv-row">
    <label class="field">
      <span>Importar CSV / Excel</span>
      <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onchange={importarCsvArticulos} />
    </label>
    <span class="csv-hint">CSV o Excel (.xlsx): codigo,nombre,unidad,stockMinimo</span>
  </div>
  <label class="field" style="max-width:320px;margin:.5rem 0">
    <span>Buscar artículo (máx. 10)</span>
    <input type="search" placeholder="Nombre o código…" bind:value={artQ} />
  </label>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>Stock</th><th>Mínimo</th><th>Costo prom.</th><th></th></tr></thead>
    <tbody>
      {#each articulosVisibles as a (a.id)}
        <tr class:bajo={Number(a.stockActual ?? 0) <= Number(a.stockMinimo ?? 0)}>
          <td>{a.codigo}</td><td>{a.nombre}</td><td>{a.stockActual ?? 0}</td><td>{a.stockMinimo ?? 0}</td><td>Q {Number(a.costoPromedio ?? 0).toFixed(2)}</td>
          <td>
            <button type="button" class="link" onclick={() => editarArticulo(a)}>Editar</button>
            <button type="button" class="link" onclick={() => trasladarAFarmacia(a)}>→ Farmacia POS</button>
            <button type="button" class="link" onclick={() => trasladarAFarmaciaHosp(a)}>→ FAR-HOSP</button>
            <button type="button" class="link" onclick={() => eliminarArticulo(a)}>Eliminar</button>
          </td>
        </tr>
      {:else}<tr><td colspan="6" class="empty">Sin artículos.</td></tr>{/each}
    </tbody>
  </table>

  <h3>Movimientos rápidos</h3>
  <form onsubmit={registrarMov} class="row">
    <label class="field grow"><span>Artículo</span>
      <SearchSelect items={artItems} bind:value={mov.articuloId} placeholder="Buscar artículo…" required />
    </label>
    <label class="field"><span>Tipo</span>
      <select bind:value={mov.tipo}><option>ENTRADA</option><option>SALIDA</option><option>AJUSTE</option><option>MERMA</option><option>TRASLADO</option></select>
    </label>
    <label class="field"><span>Cantidad</span><input type="number" placeholder="Cantidad" bind:value={mov.cantidad} style="width:100px" required/></label>
    <label class="field"><span>Costo unit.</span><input type="number" step="0.0001" placeholder="Costo unit." bind:value={mov.costoUnitario} style="width:120px"/></label>
    <label class="field"><span>Referencia</span><input placeholder="Referencia" bind:value={mov.referencia}/></label>
    <label class="field"><span>Motivo</span><input placeholder="Motivo" bind:value={mov.motivo}/></label>
    <button type="submit">Registrar</button>
  </form>

  <h3>Proveedores</h3>
  <form onsubmit={guardarProveedor} class="row">
    <label class="field"><span>NIT</span><input placeholder="NIT" bind:value={nuevoProv.nit} style="width:120px"/></label>
    <label class="field grow"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevoProv.nombre} required /></label>
    <label class="field"><span>Teléfono</span><input placeholder="Teléfono" bind:value={nuevoProv.telefono} style="width:140px"/></label>
    <label class="field"><span>Email</span><input placeholder="Email" bind:value={nuevoProv.email}/></label>
    <button type="submit">{editandoProvId ? 'Guardar' : 'Agregar'}</button>
    {#if editandoProvId}<button type="button" class="ghost" onclick={cancelarProveedor}>Cancelar</button>{/if}
  </form>
  <table>
    <thead><tr><th>NIT</th><th>Nombre</th><th>Teléfono</th><th>Email</th><th></th></tr></thead>
    <tbody>
      {#each proveedores as p (p.id)}
        <tr><td>{p.nit ?? '—'}</td><td>{p.nombre}</td><td>{p.telefono ?? ''}</td><td>{p.email ?? ''}</td><td><button type="button" class="link" onclick={() => editarProveedor(p)}>Editar</button> <button type="button" class="link" onclick={() => eliminarProveedor(p)}>Eliminar</button></td></tr>
      {:else}<tr><td colspan="5" class="empty">Sin proveedores.</td></tr>{/each}
    </tbody>
  </table>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .actions { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  button.ghost { background: #fff; color: #475569; border: 1px solid #e5e7eb; box-shadow: none; }
  button.ghost:hover:not(:disabled) { transform: none; background: #f1f5f9; }
  .link { background: none; border: 0; color: #16a34a; font-weight: 600; cursor: pointer; padding: .2rem .4rem; font-size: .8rem; }
  .link:hover { text-decoration: underline; }
  .row { display: flex; flex-wrap: wrap; gap: .5rem; align-items: end; margin-bottom: .75rem; }
  .field { display: flex; flex-direction: column; gap: .25rem; }
  .field.grow { flex: 1; min-width: 160px; }
  .field span { font-size: .7rem; font-weight: 600; color: #6b7280; text-transform: uppercase; }
  .field input, .field select { padding: .45rem .6rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; }
  .chk { display: flex; align-items: center; gap: .35rem; font-size: .85rem; color: #374151; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 1.5rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; color: #6b7280; padding: .5rem .7rem; background: #f0fdf4; }
  td { padding: .5rem .7rem; border-top: 1px solid #f3f4f6; font-size: .88rem; }
  tr.bajo td { background: #fef3c7; }
  .err { color: #dc2626; }
  .ok { color: #15803d; font-weight: 600; }
  .empty { color: #9ca3af; text-align: center; padding: 1.2rem; }
  .csv-row { display: flex; flex-wrap: wrap; gap: .75rem; align-items: end; margin: 0 0 1rem; }
  .csv-hint { font-size: .8rem; color: #6b7280; padding-bottom: .4rem; }
</style>
